import { GalleryModal } from '@components/modals/GalleryModal'
import { useModalContext } from '@fourthwall/components'
import { arrayMove } from '@fourthwall/utils/dist/array'

import type { OfferImagesFieldValue, OfferImagesFilter } from '../types'

export type UseOfferImagesProps = {
  filters: OfferImagesFilter[]
  value: OfferImagesFieldValue
  onChange: (value: OfferImagesFieldValue) => void
}

const isImageMatchingFilters =
  (filters: OfferImagesFilter[]) =>
  (image: OfferImagesFieldValue[number]): boolean => {
    return image.tags.every((tag) => {
      const filter = filters.find((filter) => filter.tag === tag.type)

      return filter ? filter.value === tag.name : true
    })
  }

const getGalleryData = ({
  value,
  filters,
}: {
  value: OfferImagesFieldValue
  filters: OfferImagesFilter[]
}) => {
  return value.filter(isImageMatchingFilters(filters))
}

const mapIndexPosition = <Item extends { position: number }>(item: Item, index: number): Item => {
  return { ...item, position: index }
}

export const useOfferGallery = ({ filters, value, onChange }: UseOfferImagesProps) => {
  const { open } = useModalContext()

  const galleryData = getGalleryData({ value, filters })

  const handleGalleryView = (item: { id: string }) => {
    open(GalleryModal, {
      images: galleryData,
      selectedImageId: `${item.id}`,
    })
  }

  const handleGalleryRemove = (item: { id: string }) => {
    onChange(value.filter(({ id }) => id !== item.id).map(mapIndexPosition))
  }

  const handleGallerySortChange = (oldIndex: number, newIndex: number) => {
    const valueOldIndex = value.findIndex(({ id }) => id === galleryData[oldIndex].id)
    const valueNewIndex = value.findIndex(({ id }) => id === galleryData[newIndex].id)

    onChange(arrayMove(value, valueOldIndex, valueNewIndex).map(mapIndexPosition))
  }

  return {
    data: galleryData,
    onImageView: handleGalleryView,
    onImageRemove: handleGalleryRemove,
    onImagesSort: handleGallerySortChange,
  }
}
